import { extensionOf, type IFolder } from '@apis/documents';

// Genba's document-store constraints, mirrored client-side so we fail fast with
// localized messages before anything reaches the upload API

// Characters Genba rejects in both file and folder names: \ : ? * " > < | / # ; %
// Kept as an explicit string (not just the regex) so the error message can list
// exactly what's disallowed.
export const FORBIDDEN_NAME_CHARS = '\\:?*"><|/#;%';
const FORBIDDEN_NAME_RE = /[\\:?*"><|/#;%]/g;

export const MAX_FILE_NAME_LENGTH = 200;
export const MAX_FOLDER_NAME_LENGTH = 20;

// Nesting limit: up to 10 levels of folders including the root. The 10th level
// may hold files only — no folder may exist deeper than this.
export const MAX_FOLDER_DEPTH = 10;

// Per-root upload ceilings. Photos are capped lower than drawings/documents.
const MB = 1024 * 1024;
export const PHOTO_MAX_BYTES = 20 * MB;
export const FILE_MAX_BYTES = 100 * MB;

// A structured result so callers map to i18n with their own `t`; keeps this
// module free of any i18n dependency and trivially unit-testable.
export type NameValidationError =
  | { code: 'empty' }
  | { code: 'invalidName' }
  | { code: 'nameTaken' }
  | { code: 'tooLong'; max: number }
  | { code: 'forbiddenChars'; chars: string };

export type SizeValidationError = { code: 'tooLarge'; maxBytes: number };

// The Genba root a folder/doc id belongs to ("photo:/Site/x" → "photo").
export function rootOfId(id: string): string {
  const colon = id.indexOf(':');
  return colon === -1 ? id : id.slice(0, colon);
}

export function maxFileSizeForFolder(folderId: string): number {
  return rootOfId(folderId) === 'photo' ? PHOTO_MAX_BYTES : FILE_MAX_BYTES;
}

function validateName(name: string, max: number): NameValidationError | null {
  const trimmed = name.trim();
  if (trimmed.length === 0) return { code: 'empty' };
  // The store refuses a name of only dots ("." and ".." read as path steps).
  if (/^\.+$/.test(trimmed)) return { code: 'invalidName' };
  if (trimmed.length > max) return { code: 'tooLong', max };
  const forbidden = trimmed.match(FORBIDDEN_NAME_RE);
  if (forbidden) {
    // De-duplicate while preserving order, so "a/b/c" reports just "/".
    const chars = [...new Set(forbidden)].join(' ');
    return { code: 'forbiddenChars', chars };
  }
  return null;
}

export function validateFileName(name: string): NameValidationError | null {
  // Nothing but dots before the extension ("...pdf") is refused like a dots-only name.
  const trimmed = name.trim();
  if (/^\.+$/.test(trimmed.slice(0, trimmed.length - extensionOf(trimmed).length))) return { code: 'invalidName' };
  return validateName(name, MAX_FILE_NAME_LENGTH);
}

/** What a folder must not be named: the names of `siblings` (the folders
 *  under the same parent), the folder itself left out by id when renaming. */
export function siblingNamesOf(siblings: readonly Pick<IFolder, 'id' | 'name'>[], exceptId: string | null): string[] {
  return siblings.filter((f) => f.id !== exceptId).map((f) => f.name);
}

/** `siblingNames`: the other folders beside it — the store refuses a repeat,
 *  and says so only as a failure, so it's caught here. Case and outer spaces aside. */
export function validateFolderName(name: string, siblingNames: readonly string[] = []): NameValidationError | null {
  const err = validateName(name, MAX_FOLDER_NAME_LENGTH);
  if (err) return err;
  const key = name.trim().toLowerCase();
  return siblingNames.some((sibling) => sibling.trim().toLowerCase() === key) ? { code: 'nameTaken' } : null;
}

export function validateFileSize(folderId: string, sizeBytes: number): SizeValidationError | null {
  const maxBytes = maxFileSizeForFolder(folderId);
  return sizeBytes > maxBytes ? { code: 'tooLarge', maxBytes } : null;
}

// Depth of a folder counting the root as level 1.
export function folderDepth(folders: IFolder[], id: string): number {
  const byId = new Map(folders.map((f) => [f.id, f]));
  let depth = 0;
  for (let node = byId.get(id); node; node = node.parentId ? byId.get(node.parentId) : undefined) {
    depth += 1;
  }
  return depth;
}

// Height of the subtree rooted at `id` (the folder alone = 1).
export function subtreeHeight(folders: IFolder[], id: string): number {
  const children = folders.filter((f) => f.parentId === id);
  if (children.length === 0) return 1;
  return 1 + Math.max(...children.map((c) => subtreeHeight(folders, c.id)));
}

// A new subfolder may be created under `parentId` only if it would not exceed
// MAX_FOLDER_DEPTH — i.e. the parent is not already at the deepest level.
export function canCreateSubfolder(folders: IFolder[], parentId: string): boolean {
  return folderDepth(folders, parentId) + 1 <= MAX_FOLDER_DEPTH;
}

// Moving `movedId` under `targetId` is allowed only if the deepest descendant of
// the moved subtree still fits within MAX_FOLDER_DEPTH at its new location.
export function canMoveFolder(folders: IFolder[], movedId: string, targetId: string): boolean {
  return folderDepth(folders, targetId) + subtreeHeight(folders, movedId) <= MAX_FOLDER_DEPTH;
}